
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DetailedBotAccount } from '@/types/trader';
import { PropCTraderAccountCard } from './PropCTraderAccountCard';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Plus, Key } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { StartChallengeDialog } from '../StartChallengeDialog';
import { PropBot } from '@/types/prop-bots';
import { toast } from 'sonner';
import { CTraderDisconnectedDialog } from '@/components/common/CTraderDisconnectedDialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslation } from 'react-i18next';
import { usePropBotAccountsManagement } from '@/hooks/use-prop-bot-accounts-management';

interface PropConnectedAccountsSectionProps {
  accounts: DetailedBotAccount[];
  isLoading: boolean;
  bot: PropBot;
}

export function PropConnectedAccountsSection({ 
  accounts: propAccounts, 
  isLoading: propIsLoading,
  bot
}: PropConnectedAccountsSectionProps) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  // Use the new management hook for enhanced functionality
  const {
    accounts,
    isLoading,
    handleToggleConnection,
    handleDeleteAccount,
    deletingAccountIds,
    disconnectedCspAccountId,
    isDisconnectedDialogOpen,
    setIsDisconnectedDialogOpen
  } = usePropBotAccountsManagement(bot.id);

  // Use prop data as fallback if hook data is not available
  const displayAccounts = accounts.length > 0 ? accounts : propAccounts;
  const displayIsLoading = isLoading || propIsLoading;

  if (displayIsLoading) {
    return (
      <div className={`space-y-${isMobile ? "4" : "6"}`} data-tour="prop-bot-accounts-section">
        <div className="animate-pulse space-y-4">
          <div className="flex items-center gap-3">
            <div className={`${isMobile ? 'p-2' : 'p-3'} bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl`}>
              <div className={`${isMobile ? "h-5 w-5" : "h-6 w-6"} bg-primary/20 rounded`} />
            </div>
            <div>
              <div className={`h-${isMobile ? "6" : "8"} w-48 bg-muted rounded`} />
              <div className={`h-4 w-64 bg-muted/60 rounded mt-1`} />
            </div>
          </div>
          <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent" />
          <div className={`${isMobile ? "h-[300px]" : "h-[400px]"} rounded-lg bg-muted`} />
        </div>
      </div>
    );
  }

  // Enhanced success handler with optimistic update support
  const handleAddAccountSuccess = async () => {
    setShowAddDialog(false);
    
    // Enhanced refresh with Promise.all for immediate data sync
    try {
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ['propBotDetail', bot.id] }),
        queryClient.refetchQueries({ queryKey: ['propBotAccounts', bot.id] })
      ]);
    } catch (error) {
      console.error('Error refreshing data after account addition:', error);
    }
  };

  const EmptyStateCard = () => (
    <div className="col-span-full border-dashed border-2 border-muted bg-gradient-to-br from-muted/20 to-muted/10 rounded-xl">
      <div className={`flex flex-col items-center justify-center ${isMobile ? 'py-12 px-4' : 'py-16 px-6'} text-center`}>
        <div className={`${isMobile ? 'mb-4 p-4' : 'mb-6 p-6'} bg-gradient-to-br from-primary/10 to-primary/5 rounded-full`}>
          <Key className={`${isMobile ? 'h-8 w-8' : 'h-10 w-10'} text-primary`} />
        </div>
        <h3 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold text-foreground mb-3`}>
          {t('no_accounts_connected')}
        </h3>
        <p className={`${isMobile ? 'text-sm' : 'text-sm'} text-muted-foreground ${isMobile ? 'mb-6' : 'mb-8'} max-w-md leading-relaxed`}>
          Connect your first trading account to start using this prop trading bot and monitor your trading activity.
        </p>
        <Button 
          onClick={() => setShowAddDialog(true)}
          size={isMobile ? "sm" : "default"}
          className="group bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]"
          data-tour="add-account-btn"
        >
          <Plus className={`${isMobile ? 'h-4 w-4 mr-1' : 'h-5 w-5 mr-2'} transition-transform duration-200 group-hover:rotate-90`} />
          {isMobile ? 'Connect Account' : t('add_new_account')}
        </Button>
      </div>
    </div>
  );

  return (
    <div className={`space-y-${isMobile ? "4" : "6"}`} data-tour="prop-bot-accounts-section">
      <div className="flex items-center gap-3">
        <div className={`${isMobile ? 'p-2' : 'p-3'} bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl`}>
          <Key className={`${isMobile ? "h-5 w-5" : "h-6 w-6"} text-primary`} />
        </div>
        <div>
          <h2 className={`${isMobile ? "text-xl" : "text-2xl"} font-bold text-foreground`}>
            {t('connected_accounts')}
          </h2>
          <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground font-medium`}>
            Manage your trading account connections for this prop bot
          </p>
        </div>
      </div>

      <Separator className="bg-gradient-to-r from-transparent via-border to-transparent" />

      {!displayAccounts.length ? (
        <EmptyStateCard />
      ) : (
        <div className={`grid grid-cols-1 ${isMobile ? 'gap-3' : 'md:grid-cols-2 lg:grid-cols-3 gap-6'}`}>
          {displayAccounts.map((account) => (
            <PropCTraderAccountCard 
              key={account.id} 
              account={account} 
              onToggleStatus={handleToggleConnection}
              onDelete={handleDeleteAccount}
              isProcessing={false}
              isDeleting={deletingAccountIds.has(account.id)}
            />
          ))}
        </div>
      )}

      {/* Add New Account Button - Fixed position */}
      <div className="flex justify-center pt-4">
        <Button 
          variant="outline"
          onClick={() => setShowAddDialog(true)}
          size={isMobile ? "sm" : "default"}
          className="border-dashed border-2 hover:border-primary/50 hover:bg-primary/5 transition-all duration-200"
          data-tour="add-account-btn"
        >
          <Plus className={`${isMobile ? "mr-1 h-3.5 w-3.5" : "mr-2 h-4 w-4"}`} />
          {t('add_new_account')}
        </Button>
      </div>

      <StartChallengeDialog
        bot={bot}
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSuccess={handleAddAccountSuccess}
      />

      <CTraderDisconnectedDialog
        open={isDisconnectedDialogOpen}
        onOpenChange={setIsDisconnectedDialogOpen}
        cspAccountId={disconnectedCspAccountId}
      />
    </div>
  );
}
